import React from 'react';
import { Styles } from '../../../components/styles/pagination.js';

const MAX_VISIBLE_PAGES = 5;

const CoursePagination = ({ meta, currentPage, onPageChange, loading }) => {
    if (!meta || !meta.total || !meta.pageSize) {
        return null;
    }

    const totalPages = Math.max(1, Math.ceil(meta.total / meta.pageSize));
    if (totalPages <= 1) {
        return null;
    }

    const safeCurrent = Math.min(Math.max(currentPage || 1, 1), totalPages);

    const handleChange = (page) => {
        if (!onPageChange || loading) {
            return;
        }
        if (page < 1 || page > totalPages || page === safeCurrent) {
            return;
        }
        onPageChange(page);
    };

    const getVisiblePages = () => {
        const half = Math.floor(MAX_VISIBLE_PAGES / 2);
        let start = Math.max(1, safeCurrent - half);
        let end = start + MAX_VISIBLE_PAGES - 1;

        if (end > totalPages) {
            end = totalPages;
            start = Math.max(1, end - MAX_VISIBLE_PAGES + 1);
        }

        const pages = [];
        for (let page = start; page <= end; page += 1) {
            pages.push(page);
        }
        return pages;
    };

    const visiblePages = getVisiblePages();
    const firstVisible = visiblePages[0];
    const lastVisible = visiblePages[visiblePages.length - 1];

    const renderPageButton = (page) => (
        <li className={`list-inline-item${safeCurrent === page ? ' active' : ''}`} key={`page-${page}`}>
            <button
                type="button"
                onClick={() => handleChange(page)}
                disabled={loading}
                aria-current={safeCurrent === page ? 'page' : undefined}
            >
                {page}
            </button>
        </li>
    );

    const renderEllipsis = (key) => (
        <li className="list-inline-item disabled" key={key}>
            <button type="button" disabled>
                ...
            </button>
        </li>
    );

    return (
        <Styles>
            <ul className="pagination-box list-unstyled list-inline">
                <li className={`list-inline-item${safeCurrent === 1 ? ' disabled' : ''}`}>
                    <button
                        type="button"
                        onClick={() => handleChange(1)}
                        disabled={loading || safeCurrent === 1}
                        aria-label="First page"
                    >
                        <i className="las la-angle-double-left" />
                    </button>
                </li>
                <li className={`list-inline-item${safeCurrent === 1 ? ' disabled' : ''}`}>
                    <button
                        type="button"
                        onClick={() => handleChange(safeCurrent - 1)}
                        disabled={loading || safeCurrent === 1}
                        aria-label="Previous page"
                    >
                        <i className="las la-angle-left" />
                    </button>
                </li>

                {firstVisible > 1 && renderPageButton(1)}
                {firstVisible > 2 && renderEllipsis('start-ellipsis')}

                {visiblePages.map(renderPageButton)}

                {lastVisible < totalPages - 1 && renderEllipsis('end-ellipsis')}
                {lastVisible < totalPages && renderPageButton(totalPages)}

                <li className={`list-inline-item${safeCurrent === totalPages ? ' disabled' : ''}`}>
                    <button
                        type="button"
                        onClick={() => handleChange(safeCurrent + 1)}
                        disabled={loading || safeCurrent === totalPages}
                        aria-label="Next page"
                    >
                        <i className="las la-angle-right" />
                    </button>
                </li>
                <li className={`list-inline-item${safeCurrent === totalPages ? ' disabled' : ''}`}>
                    <button
                        type="button"
                        onClick={() => handleChange(totalPages)}
                        disabled={loading || safeCurrent === totalPages}
                        aria-label="Last page"
                    >
                        <i className="las la-angle-double-right" />
                    </button>
                </li>
            </ul>
        </Styles>
    );
};

export default CoursePagination;
